import { dateStringToUtcNoonEpoch, todayAsDateString } from '../helper/date-time-utils';
import { DailyHouly, LocalStorageKeys, Parameters, PeriodValues } from '../types/enums';
import { ParameterConfig } from '../types/main-content.types';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  // Save a key-value pair to localStorage
  setItem(key: LocalStorageKeys, value: any): void {
    try {
      const jsonData = JSON.stringify(value);
      localStorage.setItem(key, jsonData);
    } catch (error) {
      console.error('Error saving to localStorage', error);
    }
  }

  // Retrieve a value by key from localStorage
  getItem<T>(key: LocalStorageKeys): any | null {
    try {
      const jsonData = localStorage.getItem(key);
      return jsonData ? (JSON.parse(jsonData) as T) : null;
    } catch (error) {
      console.error('Error reading from localStorage', error);
      return null;
    }
  }

  // Remove an item from localStorage
  removeItem(key: LocalStorageKeys): void {
    localStorage.removeItem(key);
  }

  // Clear all items in localStorage
  clear(): void {
    localStorage.clear();
  }

  /**
   * This method is responsible for modifying the parameters in local storage
   * When user hits reset we want to set parameter to max temp and recreate the day field
   * with the new day one as the value and the correct label
   */
  updateParamsForDayOne(): void {
    const dayOneDate = todayAsDateString();
    const dayOneEpoch = dateStringToUtcNoonEpoch(dayOneDate);

    const [, m, d] = dayOneDate.split('-').map(Number);
    const tempDate = new Date(Date.UTC(Number(dayOneDate.split('-')[0]), m - 1, d));
    const monthShort = tempDate.toLocaleDateString('default', { month: 'short', timeZone: 'UTC' });
    const label = `Day 1 ${d}-${monthShort}`;
    const currentParams = this.getItem<ParameterConfig>(LocalStorageKeys.params);

    if (currentParams && currentParams.day) {
      currentParams.day.value = dayOneDate;
      currentParams.day.label = label;
      currentParams.day.epoch = dayOneEpoch;
      currentParams.day.day = 1;
      currentParams.parameter = Parameters.MAX_TEMP;
      currentParams.dailyPeriod = DailyHouly.daily;
      currentParams.timeRange = PeriodValues['1-5day'];
      this.setItem(LocalStorageKeys.params, currentParams);
      this.setDayOne(dayOneDate);
    }
  }

  /**
   * This method clears all keys from localStorage except the ones specified
   * @param keysToKeep localStorage keys to keep (aka params and tempUnit settings which are in the header)
   */
  private clearExcept(keysToKeep: string[]): void {
    Object.values(LocalStorageKeys)
      .filter((key) => !keysToKeep.includes(key))
      .forEach((key) => localStorage.removeItem(key));
  }

  /**
   * This method clears all custom keys from localStorage except the ones specified
   * Also ensures that the params are set to max temp and the day field is updated to day one
   */
  clearCustomKeys(): void {
    const keysToKeep = [
      LocalStorageKeys.params,
      LocalStorageKeys.defaultRegion,
      LocalStorageKeys.headerSettings,
      LocalStorageKeys.copyWeightingsSelections,
      LocalStorageKeys.forecastMode,
      LocalStorageKeys.subseasonalView,
    ];

    this.clearExcept(keysToKeep);
    const currentParams = this.getItem<ParameterConfig>(LocalStorageKeys.params);

    if (currentParams) {
      currentParams.dayPeriodParameter = 'parameter';
      this.setItem(LocalStorageKeys.params, currentParams);
    }
  }

  setDayOne(formattedDate: string) {
    localStorage.setItem(LocalStorageKeys.dayOne, formattedDate);
  }
}
